import type React from 'react';
import { createContext, useEffect, useState } from 'react';

import type { AuthUser } from 'aws-amplify/auth';
import { getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth';

interface AuthContextType {
    user: AuthUser | null;
    isAuthenticated: boolean;
    loading: boolean;
    handleSignIn: () => Promise<void>;
    handleSignOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkUser = async () => {
            try {
                const currentUser = await getCurrentUser();
                setUser(currentUser);
            } catch (error) {
                console.error('No authenticated user', error);
                setUser(null);
                localStorage.clear();
            } finally {
                setLoading(false);
            }
        };

        void checkUser();
    }, []);

    const handleSignIn = async () => {
        await signInWithRedirect();
    };

    const handleSignOut = async () => {
        await signOut();
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: !!user, loading, handleSignIn, handleSignOut }}>
            {children}
        </AuthContext.Provider>
    );
};
